import React, { useEffect, useState, useMemo } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  ArrowLeft, Package, Calendar, FileText, CheckCircle, Truck, Clock, Gift, MapPin, LayoutGrid, X, Download
} from 'lucide-react';
import { getMyOrder, type BuyerOrder } from '../../services/clientService';
import api, { API_BASE_URL } from '../../services/api';
import { useAuthStore } from '../../stores/authStore';
import { useAlert } from '../../components/AlertProvider';
import { WorkflowTimeline, OrderDocuments, OrderAddresses, OrderDetailTabs } from '../../components/order';

interface TierInfo {
  tier_number: number;
  min_quantity: number;
  discount_percentage?: number | null;
  free_units?: number | null;
  client_price: number;
  triggered: boolean;
}

interface UnitTypeTierData {
  unit_name: string;
  marque?: string;
  gamme?: string;
  batch_size: number;
  total_lots_ordered: number;
  catalog_price: number;
  tiers: TierInfo[];
  tier_mode: 'ug' | 'percent';
}

interface TierBreakdown {
  [key: string]: UnitTypeTierData;
}

const statusConfig = {
  pending: { label: 'En attente', color: 'bg-yellow-100 text-yellow-700 dark:bg-yellow-950/30 dark:text-yellow-300', icon: Clock },
  confirmed: { label: 'Confirmée', color: 'bg-blue-100 text-blue-700 dark:bg-blue-950/30 dark:text-blue-300', icon: CheckCircle },
  shipped: { label: 'Expédiée', color: 'bg-purple-100 text-purple-700 dark:bg-purple-950/30 dark:text-purple-300', icon: Truck },
  delivered: { label: 'Livrée', color: 'bg-green-100 text-green-700 dark:bg-green-950/30 dark:text-green-300', icon: CheckCircle },
  invoiced: { label: 'Facturée', color: 'bg-amber-100 text-amber-700 dark:bg-amber-950/30 dark:text-amber-300', icon: FileText },
  paid: { label: 'Payée', color: 'bg-emerald-100 text-emerald-700 dark:bg-emerald-950/30 dark:text-emerald-300', icon: CheckCircle },
  cancelled: { label: 'Annulée', color: 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-300', icon: CheckCircle },
} as const;

const ClientOrderDetailNew: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { showSuccess, showError } = useAlert();
  const [order, setOrder] = useState<BuyerOrder | null>(null);
  const [loading, setLoading] = useState(true);
  const [pdfType, setPdfType] = useState<'bdc' | 'bdl' | 'invoice' | null>(null);
  const [loadingPdf, setLoadingPdf] = useState<boolean>(false);
  const [pdfUrl, setPdfUrl] = useState<string | null>(null);
  const [hasInvoice, setHasInvoice] = useState<boolean>(false);
  const [invoiceIssueDate, setInvoiceIssueDate] = useState<string | null>(null);

  useEffect(() => { void loadOrder(); }, [id]);

  useEffect(() => {
    if (order) {
      void checkInvoiceAvailability();
    }
  }, [order?.id]);

  const loadOrder = async (): Promise<void> => {
    if (!id) return;
    try {
      setLoading(true);
      const data = await getMyOrder(parseInt(id, 10));
      const hasValidOrderDate = data.order_date && !Number.isNaN(Date.parse(data.order_date));
      const orderDateValue = hasValidOrderDate ? data.order_date : data.created_at;
      setOrder({ ...data, order_date: orderDateValue });
    } catch (error: any) {
      showError(error?.response?.data?.detail ?? 'Impossible de charger la commande');
      navigate('/client/orders');
    } finally {
      setLoading(false);
    }
  };

  const checkInvoiceAvailability = async (): Promise<void> => {
    if (!order) return;
    try {
      const invoiceResponse = await api.get(`/clients/me/orders/${order.id}/invoice`, {
        validateStatus: (status) => status === 200 || status === 204,
      });

      if (invoiceResponse.status === 200 && invoiceResponse.data?.id) {
        setHasInvoice(true);
        setInvoiceIssueDate(invoiceResponse.data.issue_date || null);
      } else {
        setHasInvoice(false);
        setInvoiceIssueDate(null);
      }
    } catch {
      setHasInvoice(false);
      setInvoiceIssueDate(null);
    }
  };

  const handleViewPdf = async (type: 'bdc' | 'bdl' | 'invoice'): Promise<void> => {
    if (!order) return;

    if (pdfType === type) {
      closePdfViewer();
      return;
    }

    setPdfType(type);
    setLoadingPdf(true);
    setPdfUrl(null);

    const token = useAuthStore.getState().token;
    if (!token) { setLoadingPdf(false); return; }

    try {
      if (type === 'bdc') {
        setPdfUrl(`${API_BASE_URL}/clients/me/orders/${order.id}/bdc/pdf?token=${encodeURIComponent(token)}`);
      } else if (type === 'bdl') {
        setPdfUrl(`${API_BASE_URL}/clients/me/orders/${order.id}/delivery-note/pdf?token=${encodeURIComponent(token)}`);
      } else if (type === 'invoice') {
        const invoiceResponse = await api.get(`/clients/me/orders/${order.id}/invoice`, {
          validateStatus: (status) => status === 200 || status === 204,
        });

        if (invoiceResponse.status === 204 || !invoiceResponse.data?.id) {
          throw new Error('Aucune facture disponible pour cette commande.');
        }

        const invoiceId: number = invoiceResponse.data.id;
        setPdfUrl(`${API_BASE_URL}/clients/me/invoices/${invoiceId}/pdf?token=${encodeURIComponent(token)}`);
      }
    } catch (error: any) {
      console.error('Erreur chargement PDF:', error);
      showError(error?.message || 'Impossible de charger le PDF.');
      setPdfType(null);
      setPdfUrl(null);
    } finally {
      setLoadingPdf(false);
    }
  };

  const closePdfViewer = (): void => {
    setPdfType(null);
    setPdfUrl(null);
  };

  const handleConfirmReception = async (): Promise<void> => {
    if (!order) return;
    try {
      await api.post(`/purchase-orders/${order.id}/confirm-reception`);
      showSuccess('Réception confirmée.');
      await loadOrder();
    } catch (error: any) { showError(error?.response?.data?.detail ?? 'Erreur.'); }
  };

  const tierBreakdownParsed = useMemo<TierBreakdown | null>(() => {
    if (!order?.tier_breakdown) return null;
    try {
      return JSON.parse(order.tier_breakdown) as TierBreakdown;
    } catch {
      return null;
    }
  }, [order?.tier_breakdown]);

  if (loading) return <div className="flex items-center justify-center h-screen"><div className="animate-spin rounded-full h-12 w-12 border-b-2 border-orange-600"></div></div>;
  if (!order) return null;

  const status = statusConfig[order.status] || statusConfig.pending;
  const StatusIcon = status.icon;
  const totalAmount = order.total_amount || 0;
  const subtotalAmount = order.subtotal || 0;
  const deliveryFeeAmount = order.delivery_fee || 0;
  const taxAmount = order.tax_amount || 0;

  // Calcul des économies totales
  const totalSavings = (order.items || []).reduce((acc, item) => {
    const hasDiscount = item.discount_percentage > 0;
    if (!hasDiscount) return acc;
    const priceBeforeDiscount = item.unit_price / (1 - item.discount_percentage / 100);
    const lineTotalBeforeDiscount = priceBeforeDiscount * item.quantity;
    return acc + (lineTotalBeforeDiscount - item.line_total);
  }, 0);

  const subtotalBeforeDiscount = subtotalAmount + totalSavings;
  const globalDiscountPct = subtotalBeforeDiscount > 0 ? (totalSavings / subtotalBeforeDiscount) * 100 : 0;

  const taxBase = subtotalAmount + deliveryFeeAmount;
  const taxRate = taxBase > 0 ? (taxAmount / taxBase) * 100 : 0;

  const formatDate = (dateStr: string | null | undefined): string => {
    if (!dateStr) return 'Non définie';
    try {
      const date = new Date(dateStr);
      if (isNaN(date.getTime())) return 'Date invalide';
      return date.toLocaleDateString('fr-FR', { day: '2-digit', month: 'long', year: 'numeric' });
    } catch (e) {
      return 'Date invalide';
    }
  };

  const formatTaxRate = (v: number): string => v.toFixed(2).replace(/\.00$/, '');
  const getUnitLabel = (v: string): string => v.replace(/\s*\(Lot de\s*\d+\)/, '').trim();

  const renderArticlesContent = () => {
    if (!order.items?.length) return <div className="text-center py-8 text-slate-400"><Package className="w-12 h-12 mx-auto mb-3 opacity-50" /><p>Aucun article</p></div>;

    return (
      <div className="space-y-4">
        {order.items.map((item) => {
          let tierInfo: { activeTier: TierInfo } | null = null;
          if (tierBreakdownParsed) {
            for (const data of Object.values(tierBreakdownParsed)) {
              const activeTier = data.tiers.find((t) => t.triggered);
              if (activeTier) { tierInfo = { activeTier }; break; }
            }
          }
          const hasDiscount = item.discount_percentage > 0;
          const priceBeforeDiscount = hasDiscount ? item.unit_price / (1 - item.discount_percentage / 100) : item.unit_price;
          const lineTotalBeforeDiscount = priceBeforeDiscount * item.quantity;
          const itemSavings = lineTotalBeforeDiscount - item.line_total;

          const batchSize = item.batch_size || 1;
          const pricePerUnit = item.unit_price / batchSize;
          const pricePerUnitBefore = hasDiscount ? priceBeforeDiscount / batchSize : pricePerUnit;
          const unitLabel = getUnitLabel(item.product_unit) || 'unité';

          return (
            <div key={item.id} className="p-4 bg-slate-50 dark:bg-slate-800/50 rounded-xl border border-slate-200 dark:border-slate-700 hover:shadow-md transition-shadow">
              <div className="flex flex-col gap-4">
                <div className="flex items-start justify-between gap-3">
                  <div className="flex-1 min-w-0">
                    <h3 className="font-semibold text-slate-900 dark:text-slate-100 truncate">{item.product_title || `Article #${item.consumable_id}`}</h3>
                    <div className="flex items-center gap-2 mt-1 flex-wrap">
                      <span className="text-sm text-slate-500">{item.quantity} × {item.product_unit}</span>
                      {batchSize > 1 && <span className="text-xs px-2 py-0.5 bg-slate-200 dark:bg-slate-700 rounded-full">Lot de {batchSize} unité</span>}
                    </div>
                  </div>
                  {tierInfo && <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full bg-emerald-100 dark:bg-emerald-900/40 text-emerald-700 dark:text-emerald-300 text-xs font-semibold"><Gift className="h-3 w-3" />Palier {tierInfo.activeTier.tier_number}</span>}
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 pt-3 border-t border-slate-200 dark:border-slate-700">
                  <div>
                    <p className="text-xs text-slate-500 mb-1">Prix standard HT</p>
                    <div className="flex items-baseline gap-2">
                      <span className="text-lg font-bold text-slate-900 dark:text-slate-100">
                        {lineTotalBeforeDiscount.toFixed(2)} € HT
                      </span>
                    </div>
                    <p className="text-xs text-slate-400 mt-1">
                      {item.quantity} × {pricePerUnitBefore.toFixed(2)} € HT ({unitLabel})
                    </p>
                  </div>
                  <div>
                    <p className="text-xs text-slate-500 mb-1">Prix remisé HT</p>
                    <div className="flex items-baseline gap-2">
                      <span className="text-lg font-bold text-slate-900 dark:text-slate-100">
                        {item.line_total.toFixed(2)} € HT
                      </span>
                      {hasDiscount && (
                        <span className="text-xs px-1.5 py-0.5 rounded bg-emerald-100 dark:bg-emerald-900/40 text-emerald-700 font-medium">
                          −{item.discount_percentage}%
                        </span>
                      )}
                    </div>
                    <p className="text-xs text-slate-400 mt-1">
                      {item.quantity} × {pricePerUnit.toFixed(2)} € HT ({unitLabel})
                    </p>
                  </div>
                  <div className="sm:text-right">
                    <p className="text-xs text-slate-500 mb-1">Gain apporté</p>
                    <div className="flex flex-col sm:items-end gap-1">
                      {hasDiscount ? (
                        <div className="inline-flex items-center gap-1.5 px-2 py-0.5 bg-emerald-50 dark:bg-emerald-900/30 border border-emerald-200 dark:border-emerald-800 rounded-md shadow-sm">
                          <Gift className="w-3 h-3 text-emerald-600 dark:text-emerald-400" />
                          <span className="text-sm font-bold text-emerald-700 dark:text-emerald-300">
                            −{itemSavings.toFixed(2)} € HT
                          </span>
                        </div>
                      ) : (
                        <span className="text-sm text-slate-400">0.00 € HT</span>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          );
        })}
        <div className="mt-6 p-4 bg-gradient-to-r from-slate-100 to-slate-50 dark:from-slate-800 dark:to-slate-800/50 rounded-xl border border-slate-200 dark:border-slate-700">
          <div className="space-y-2">
            <div className="flex justify-between text-sm text-slate-600"><span>Sous-total HT</span><span className="font-medium">{subtotalAmount.toFixed(2)} €</span></div>
            <div className="flex justify-between text-sm text-slate-600"><span>Frais de livraison HT</span><span className="font-medium">{deliveryFeeAmount.toFixed(2)} €</span></div>
            {((order as any).credit_note_amount > 0 || (order as any).credit_note_percentage > 0) && (
              <>
                <div className="flex justify-between text-sm text-amber-600">
                  <span>Remise supplémentaire {(order as any).credit_note_type === 'percentage' && (order as any).credit_note_percentage > 0 ? `(${(order as any).credit_note_percentage}%)` : ''} {(order as any).credit_note_reason ? `– ${(order as any).credit_note_reason}` : ''}</span>
                  <span className="font-semibold">-{((order as any).credit_note_amount || 0).toFixed(2)} €</span>
                </div>
                <div className="flex justify-between text-sm font-semibold">
                  <span className="text-slate-700 dark:text-slate-300">Net HT</span>
                  <span className="text-slate-900 dark:text-slate-100">{(subtotalAmount - ((order as any).credit_note_amount || 0)).toFixed(2)} €</span>
                </div>
              </>
            )}
            <div className="flex justify-between text-sm text-slate-600"><span>TVA ({formatTaxRate(taxRate)}%)</span><span className="font-medium">{taxAmount.toFixed(2)} €</span></div>

            {totalSavings > 0 && (
              <div className="flex justify-between text-sm font-semibold text-emerald-600 dark:text-emerald-400 pt-1 border-t border-slate-200 dark:border-slate-700">
                <span className="flex items-center gap-1"><Gift className="w-3.5 h-3.5" /> Économies réalisées ({globalDiscountPct.toFixed(1)}%)</span>
                <span>−{totalSavings.toFixed(2)} €</span>
              </div>
            )}

            <div className="flex justify-between pt-3 border-t border-slate-300 dark:border-slate-600">
              <span className="text-lg font-bold text-slate-900 dark:text-slate-100">Total TTC</span>
              <span className="text-2xl font-bold text-orange-600">{totalAmount.toFixed(2)} €</span>
            </div>
          </div>
        </div>
      </div>
    );
  };

  const renderWorkflowContent = () => (
    <div className="space-y-6">
      <WorkflowTimeline status={order.status} orderDate={order.order_date} deliveredAt={order.delivered_at} paidAt={order.paid_at} />
      {(order.tracking_number || order.estimated_delivery_date) && (
        <div className="p-4 bg-purple-50 dark:bg-purple-900/20 rounded-xl border border-purple-200 dark:border-purple-800">
          <h4 className="font-semibold text-purple-700 dark:text-purple-300 mb-3 flex items-center gap-2"><Truck className="w-4 h-4" />Suivi</h4>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 text-sm">
            {order.tracking_number && <div><p className="text-purple-600/70">N° suivi</p><p className="font-mono font-medium text-purple-800 dark:text-purple-200">{order.tracking_number}</p></div>}
            {order.estimated_delivery_date && <div><p className="text-purple-600/70">Livraison estimée</p><p className="font-medium text-purple-800 dark:text-purple-200">{formatDate(order.estimated_delivery_date)}</p></div>}
          </div>
        </div>
      )}
      {order.status === 'delivered' && (
        <div className={`p-4 rounded-xl border ${order.received_at ? 'bg-emerald-50 dark:bg-emerald-900/20 border-emerald-200' : 'bg-blue-50 dark:bg-blue-900/20 border-blue-200'}`}>
          {order.received_at ? (
            <div className="flex items-center gap-3"><CheckCircle className="w-5 h-5 text-emerald-600" /><div><p className="font-medium text-emerald-700">Réception confirmée</p><p className="text-sm text-emerald-600/70">{formatDate(order.received_at)}</p></div></div>
          ) : (
            <div className="flex items-center justify-between gap-4">
              <div className="flex items-center gap-3"><Package className="w-5 h-5 text-blue-600" /><p className="font-medium text-blue-700">En attente de confirmation</p></div>
              <button onClick={() => void handleConfirmReception()} className="px-4 py-2 bg-emerald-600 hover:bg-emerald-700 text-white rounded-lg text-sm font-medium">J'ai bien reçu</button>
            </div>
          )}
        </div>
      )}
    </div>
  );

  const tabs = [
    {
      id: 'resume', label: 'Résumé', icon: <LayoutGrid className="w-4 h-4" />, content: (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div className="p-5 bg-gradient-to-br from-orange-50 to-amber-50 dark:from-orange-950/30 dark:to-amber-950/30 rounded-xl border-2 border-orange-200 dark:border-orange-800">
            <h3 className="font-semibold text-slate-900 dark:text-slate-100 mb-4">Résumé financier</h3>
            <div className="space-y-2 text-sm">
              <div className="flex justify-between"><span className="text-slate-600">Sous-total HT</span><span className="font-medium">{subtotalAmount.toFixed(2)} €</span></div>
              <div className="flex justify-between"><span className="text-slate-600">Livraison HT</span><span className="font-medium">{deliveryFeeAmount.toFixed(2)} €</span></div>
              {((order as any).credit_note_amount > 0 || (order as any).credit_note_percentage > 0) && (
                <>
                  <div className="flex justify-between text-amber-600">
                    <span>Remise supplémentaire {(order as any).credit_note_type === 'percentage' && (order as any).credit_note_percentage > 0 ? `(${(order as any).credit_note_percentage}%)` : ''} {(order as any).credit_note_reason ? `– ${(order as any).credit_note_reason}` : ''}</span>
                    <span className="font-semibold">-{((order as any).credit_note_amount || 0).toFixed(2)} €</span>
                  </div>
                  <div className="flex justify-between font-semibold">
                    <span className="text-slate-700 dark:text-slate-300">Net HT</span>
                    <span className="text-slate-900 dark:text-slate-100">{(subtotalAmount - ((order as any).credit_note_amount || 0)).toFixed(2)} €</span>
                  </div>
                </>
              )}
              <div className="flex justify-between"><span className="text-slate-600">TVA ({formatTaxRate(taxRate)}%)</span><span className="font-medium">{taxAmount.toFixed(2)} €</span></div>

              {tierBreakdownParsed && Object.values(tierBreakdownParsed).some(data => data.tiers.some(t => t.triggered)) && (
                <div className="pt-2 border-t border-orange-200/50">
                  <p className="text-xs font-semibold text-emerald-700 dark:text-emerald-400 mb-2 flex items-center gap-1">
                    <Gift className="w-3.5 h-3.5" /> Paliers activés
                  </p>
                  {Object.values(tierBreakdownParsed).map((data, idx) => {
                    const activeTier = data.tiers.find(t => t.triggered);
                    if (!activeTier) return null;
                    const tierGain = data.catalog_price > 0 && activeTier.client_price > 0
                      ? (data.catalog_price - activeTier.client_price) * data.total_lots_ordered
                      : 0;
                    const tierPct = activeTier.discount_percentage ??
                      (activeTier.free_units && data.catalog_price > 0
                        ? ((activeTier.free_units * data.catalog_price) / (data.total_lots_ordered * data.catalog_price)) * 100
                        : 0);
                    return (
                      <div key={idx} className="flex justify-between text-xs text-emerald-600 dark:text-emerald-400 mb-1">
                        <span>{data.unit_name} - Palier {activeTier.tier_number} ({tierPct.toFixed(1)}%)</span>
                        <span className="font-medium">−{tierGain.toFixed(2)} €</span>
                      </div>
                    );
                  })}
                </div>
              )}

              {totalSavings > 0 && (
                <div className="flex justify-between text-sm font-semibold text-emerald-700 dark:text-emerald-400 pt-2 border-t border-orange-200/50">
                  <span className="flex items-center gap-1"><Gift className="w-3.5 h-3.5" /> Gains réalisés ({globalDiscountPct.toFixed(1)}%)</span>
                  <span>−{totalSavings.toFixed(2)} €</span>
                </div>
              )}

              <div className="flex justify-between pt-3 border-t border-orange-200"><span className="font-bold">Total TTC</span><span className="text-xl font-bold text-orange-600">{totalAmount.toFixed(2)} €</span></div>
            </div>
          </div>
          <div className="p-5 bg-slate-50 dark:bg-slate-800/50 rounded-xl border border-slate-200 dark:border-slate-700">
            <h3 className="font-semibold text-slate-900 dark:text-slate-100 mb-4">Informations</h3>
            <div className="space-y-3 text-sm">
              <div className="flex items-center gap-3"><Calendar className="w-4 h-4 text-slate-400" /><div><p className="text-slate-500">Date de commande</p><p className="font-medium">{formatDate(order.order_date)}</p></div></div>
              {order.delivered_at && <div className="flex items-center gap-3"><Truck className="w-4 h-4 text-slate-400" /><div><p className="text-slate-500">Date de livraison</p><p className="font-medium">{formatDate(order.delivered_at)}</p></div></div>}
              {order.notes && <div className="pt-3 border-t border-slate-200"><p className="text-slate-500 mb-1">Notes</p><p className="text-slate-700 whitespace-pre-wrap">{order.notes}</p></div>}
            </div>
          </div>
        </div>
      )
    },
    { id: 'articles', label: 'Articles', icon: <Package className="w-4 h-4" />, badge: order.items?.length || 0, content: renderArticlesContent() },
    { id: 'workflow', label: 'Workflow', icon: <Clock className="w-4 h-4" />, content: renderWorkflowContent() },
    { id: 'documents', label: 'Documents', icon: <FileText className="w-4 h-4" />, content: <OrderDocuments status={order.status} orderDate={order.order_date} shippedAt={order.delivered_at} invoicedAt={invoiceIssueDate} hasInvoice={hasInvoice} onDownloadBdc={() => void handleViewPdf('bdc')} onDownloadBdl={() => void handleViewPdf('bdl')} onDownloadInvoice={() => void handleViewPdf('invoice')} /> },
    { id: 'adresses', label: 'Adresses', icon: <MapPin className="w-4 h-4" />, content: <OrderAddresses shippingAddress={{ type: 'shipping', address: order.delivery_address, city: order.delivery_city, postalCode: order.delivery_postal_code, country: order.delivery_country }} invoiceEmail={order.invoice_email ?? undefined} /> }
  ];

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-orange-50/20 to-slate-50 dark:from-slate-950 dark:via-orange-950/10 dark:to-slate-950">
      <div className="max-w-6xl mx-auto p-4 sm:p-6">
        {/* Header Premium */}
        <div className="mb-6">
          <button onClick={() => navigate('/client/orders')} className="flex items-center gap-2 text-slate-500 hover:text-slate-700 dark:hover:text-slate-300 mb-4 transition-colors">
            <ArrowLeft className="h-4 w-4" /><span className="text-sm">Retour aux commandes</span>
          </button>
          <div className="bg-white dark:bg-slate-900 rounded-2xl border-2 border-slate-200 dark:border-slate-700 p-6 shadow-sm">
            <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-4">
              <div className="flex items-center gap-4">
                <div className="w-14 h-14 rounded-2xl bg-gradient-to-br from-orange-500 to-amber-500 flex items-center justify-center shadow-lg">
                  <Package className="h-7 w-7 text-white" />
                </div>
                <div>
                  <h1 className="text-2xl font-bold text-slate-900 dark:text-slate-100">{order.order_number}</h1>
                  <p className="text-slate-500 text-sm">{formatDate(order.order_date)}</p>
                </div>
              </div>
              <div className="flex items-center gap-4">
                <div className={`inline-flex items-center gap-2 px-4 py-2 rounded-full text-sm font-medium ${status.color}`}>
                  <StatusIcon className="h-4 w-4" />{status.label}
                </div>
                <div className="text-right">
                  <p className="text-sm text-slate-500">Total TTC</p>
                  <p className="text-2xl font-bold text-orange-600">{totalAmount.toFixed(2)} €</p>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Tabs/Accordion Content */}
        <div className="bg-white dark:bg-slate-900 rounded-2xl border-2 border-slate-200 dark:border-slate-700 p-6 shadow-sm">
          <OrderDetailTabs tabs={tabs} defaultTab="resume" />
        </div>
      </div>

      {/* Modal PDF Viewer */}
      {pdfType !== null && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-[90] p-0 sm:p-4" onClick={closePdfViewer}>
          <div className="bg-white dark:bg-slate-900 sm:rounded-2xl shadow-2xl w-full h-full sm:max-w-5xl sm:h-[85vh] border-0 sm:border border-slate-200 dark:border-slate-800 animate-in zoom-in-95 fade-in duration-200 flex flex-col" onClick={(e) => e.stopPropagation()}>
            <div className="p-4 border-b border-slate-200 dark:border-slate-800 flex items-center justify-between">
              <h4 className="text-sm font-semibold text-slate-700 dark:text-slate-300">
                {pdfType === 'bdc' && '📄 Bon de Commande'}
                {pdfType === 'bdl' && '🚚 Bon de Livraison'}
                {pdfType === 'invoice' && '🧾 Facture'}
              </h4>
              <div className="flex items-center gap-2">
                <a
                  href={pdfUrl || '#'}
                  download
                  className="p-1.5 text-slate-500 hover:text-slate-700 hover:bg-slate-100 dark:hover:bg-slate-800 rounded-lg transition-colors"
                  title="Télécharger"
                  aria-label="Télécharger le PDF"
                >
                  <Download className="h-4 w-4" />
                </a>
                <button onClick={closePdfViewer} className="p-1.5 text-slate-500 hover:text-slate-700 hover:bg-slate-100 dark:hover:bg-slate-800 rounded-lg transition-colors">
                  <X className="h-4 w-4" />
                </button>
              </div>
            </div>
            <div className="flex-1 p-4">
              {loadingPdf ? (
                <div className="flex items-center justify-center h-full bg-slate-50 dark:bg-slate-800 rounded-lg">
                  <div className="text-center">
                    <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-orange-600 mx-auto mb-3"></div>
                    <p className="text-sm text-slate-600 dark:text-slate-400">Chargement du PDF...</p>
                  </div>
                </div>
              ) : pdfUrl ? (
                <iframe src={pdfUrl} className="w-full h-full rounded-lg border-2 border-slate-200 dark:border-slate-700 bg-white" title={`PDF ${pdfType}`} />
              ) : (
                <div className="flex items-center justify-center h-full bg-slate-50 dark:bg-slate-800 rounded-lg">
                  <p className="text-sm text-slate-600 dark:text-slate-400">Impossible de charger le PDF</p>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ClientOrderDetailNew;
